import type { Consent } from '../analytics/metaPixel';

/**
 * The consent banner's answer, told to whoever is listening at the moment it is given.
 *
 * An event that fires once, on a page's load, is lost when the banner is answered after it: the
 * pixel sends nothing without a yes. The return from Stripe is the page that matters — StartTrial
 * is reported there on load, and a visitor from an ad often answers the banner ON that page (F23,
 * 2026-09-30). Its reporter is safe to run again (state/trial.ts records only what went), so it
 * listens here and runs again on a yes.
 */
type Listener = (choice: Consent) => void;

const listeners = new Set<Listener>();

/** Listen for the banner's answer. Returns the way to stop. */
export function onConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by the banner once the answer is saved. */
export function announceConsent(choice: Consent): void {
  for (const listener of [...listeners]) {
    try {
      listener(choice);
    } catch {
      /* one listener's failure is not the banner's, nor the next listener's */
    }
  }
}
